// InformacionContractual.js

import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  Autocomplete,
  IconButton
} from '@mui/material';
import { Clear } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/**
 * Este componente muestra/edita:
 * - Agencia
 * - Rango Salarial (rangoPrecios)
 * - Contrato
 * - Buyout
 */

function InformacionContractual({
  player,
  editablePlayer,
  setEditablePlayer,
  isEditing,
  isMobile,
  agencies = [],
  priceRanges = [],
  contratos = [],
  buyoutOptions = [],
  userRole
}) {
  const { t } = useTranslation();

  // Render para MOBILE
  if (isMobile) {
    return (
      <Box
        sx={{
          mb: 2,
          backgroundColor: 'background.default'
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 'medium',
            px: 2,
            py: 1,
            backgroundColor: 'background.paper'
          }}
        >
          5. {t('createPlayer.sections.contractInfo')}
        </Typography>

        <Box sx={{ p: 2 }}>
          {isEditing ? (
            <Grid container spacing={2}>
              {/* Agencia */}
              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  options={agencies}
                  inputValue={editablePlayer.agencia || ''}
                  onInputChange={(event, newValue) => {
                    setEditablePlayer((prev) => ({
                      ...prev,
                      agencia: newValue
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('createPlayer.fields.agency')}
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {params.InputProps.endAdornment}
                            {editablePlayer.agencia && (
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditablePlayer((prev) => ({
                                    ...prev,
                                    agencia: ''
                                  }));
                                }}
                                size="small"
                              >
                                <Clear fontSize="small" />
                              </IconButton>
                            )}
                          </>
                        )
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Rango Salarial */}
              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  options={priceRanges}
                  inputValue={editablePlayer.rangoPrecios || ''}
                  onInputChange={(event, newValue) => {
                    setEditablePlayer((prev) => ({
                      ...prev,
                      rangoPrecios: newValue
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('createPlayer.fields.salaryRange')}
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {params.InputProps.endAdornment}
                            {editablePlayer.rangoPrecios && (
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditablePlayer((prev) => ({
                                    ...prev,
                                    rangoPrecios: ''
                                  }));
                                }}
                                size="small"
                              >
                                <Clear fontSize="small" />
                              </IconButton>
                            )}
                          </>
                        )
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Contrato */}
              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  options={contratos}
                  inputValue={editablePlayer.contrato || ''}
                  onInputChange={(event, newValue) => {
                    setEditablePlayer((prev) => ({
                      ...prev,
                      contrato: newValue
                    }));
                  }}
                  getOptionLabel={(option) => t(`createPlayer.contractDurations.${option}`, option)}
                  renderOption={(props, option) => (
                    <li {...props}>
                      {t(`createPlayer.contractDurations.${option}`, option)}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('createPlayer.fields.contract')}
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {params.InputProps.endAdornment}
                            {editablePlayer.contrato && (
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditablePlayer((prev) => ({
                                    ...prev,
                                    contrato: ''
                                  }));
                                }}
                                size="small"
                              >
                                <Clear fontSize="small" />
                              </IconButton>
                            )}
                          </>
                        )
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Buyout */}
              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  options={buyoutOptions}
                  inputValue={editablePlayer.buyout || ''}
                  onInputChange={(event, newValue) => {
                    setEditablePlayer((prev) => ({
                      ...prev,
                      buyout: newValue
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('createPlayer.fields.buyout')}
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {params.InputProps.endAdornment}
                            {editablePlayer.buyout && (
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditablePlayer((prev) => ({
                                    ...prev,
                                    buyout: ''
                                  }));
                                }}
                                size="small"
                              >
                                <Clear fontSize="small" />
                              </IconButton>
                            )}
                          </>
                        )
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          ) : (
            // READ ONLY (MOBILE)
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {!!player.agencia && (
                <Typography>
                  <strong>{t('createPlayer.fields.agency')}:</strong>{' '}
                  {userRole === 'entrenador' || userRole === 'scout interne' || userRole === 'scout externe' || userRole === 'scout interne cantera' || userRole === 'scout externe cantera' ? (
                    player.agencia
                  ) : (
                    <a
                      href={`/agencia?agency=${encodeURIComponent(player.agencia)}`}
                      style={{ textDecoration: 'none', color: '#1976d2' }}
                    >
                      {player.agencia}
                    </a>
                  )}
                </Typography>
              )}
              {!!player.rangoPrecios && (
                <Typography>
                  <strong>{t('createPlayer.fields.salaryRange')}:</strong> {player.rangoPrecios}
                </Typography>
              )}
              {!!player.contrato && (
                <Typography>
                  <strong>{t('createPlayer.fields.contract')}:</strong> {t(`createPlayer.contractDurations.${player.contrato}`, player.contrato)}
                </Typography>
              )}
              {!!player.buyout && (
                <Typography>
                  <strong>{t('createPlayer.fields.buyout')}:</strong> {player.buyout}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // Render para DESKTOP
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        backgroundColor: 'background.paper',
        boxShadow: '0 1px 3px rgba(16,24,40,0.04)'
      }}
    >
      <Typography
        variant="h6"
        gutterBottom
        sx={{
          fontSize: '1rem',
          fontWeight: 600,
          color: 'text.primary',
          letterSpacing: '-0.01em',
          mb: 1,
          pb: 0.5,
          borderBottom: '1px solid',
          borderColor: 'primary.main'
        }}
      >
        5. {t('createPlayer.sections.contractInfo')}
      </Typography>

      {isEditing ? (
        <Grid container spacing={2}>
          {/* Agencia */}
          <Grid item xs={12} md={3}>
            <Autocomplete
              freeSolo
              options={agencies}
              inputValue={editablePlayer.agencia || ''}
              onInputChange={(event, newValue) => {
                setEditablePlayer((prev) => ({
                  ...prev,
                  agencia: newValue
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('createPlayer.fields.agency')}
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {params.InputProps.endAdornment}
                        {editablePlayer.agencia && (
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditablePlayer((prev) => ({
                                ...prev,
                                agencia: ''
                              }));
                            }}
                            size="small"
                          >
                            <Clear fontSize="small" />
                          </IconButton>
                        )}
                      </>
                    )
                  }}
                />
              )}
            />
          </Grid>

          {/* Rango Salarial */}
          <Grid item xs={12} md={3}>
            <Autocomplete
              freeSolo
              options={priceRanges}
              inputValue={editablePlayer.rangoPrecios || ''}
              onInputChange={(event, newValue) => {
                setEditablePlayer((prev) => ({
                  ...prev,
                  rangoPrecios: newValue
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('createPlayer.fields.salaryRange')}
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {params.InputProps.endAdornment}
                        {editablePlayer.rangoPrecios && (
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditablePlayer((prev) => ({
                                ...prev,
                                rangoPrecios: ''
                              }));
                            }}
                            size="small"
                          >
                            <Clear fontSize="small" />
                          </IconButton>
                        )}
                      </>
                    )
                  }}
                />
              )}
            />
          </Grid>

          {/* Contrato */}
          <Grid item xs={12} md={3}>
            <Autocomplete
              freeSolo
              options={contratos}
              inputValue={editablePlayer.contrato || ''}
              onInputChange={(event, newValue) => {
                setEditablePlayer((prev) => ({
                  ...prev,
                  contrato: newValue
                }));
              }}
              getOptionLabel={(option) => t(`createPlayer.contractDurations.${option}`, option)}
              renderOption={(props, option) => (
                <li {...props}>
                  {t(`createPlayer.contractDurations.${option}`, option)}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('createPlayer.fields.contract')}
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {params.InputProps.endAdornment}
                        {editablePlayer.contrato && (
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditablePlayer((prev) => ({
                                ...prev,
                                contrato: ''
                              }));
                            }}
                            size="small"
                          >
                            <Clear fontSize="small" />
                          </IconButton>
                        )}
                      </>
                    )
                  }}
                />
              )}
            />
          </Grid>

          {/* Buyout */}
          <Grid item xs={12} md={3}>
            <Autocomplete
              freeSolo
              options={buyoutOptions}
              inputValue={editablePlayer.buyout || ''}
              onInputChange={(event, newValue) => {
                setEditablePlayer((prev) => ({
                  ...prev,
                  buyout: newValue
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('createPlayer.fields.buyout')}
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {params.InputProps.endAdornment}
                        {editablePlayer.buyout && (
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditablePlayer((prev) => ({
                                ...prev,
                                buyout: ''
                              }));
                            }}
                            size="small"
                          >
                            <Clear fontSize="small" />
                          </IconButton>
                        )}
                      </>
                    )
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      ) : (
        // READ ONLY (DESKTOP)
        <Box sx={{ mt: 0.5 }}>
          <Grid container rowSpacing={1} columnSpacing={2.5}>
            {!!player.agencia && (
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.agency')}:
                  </Typography>
                  {userRole === 'entrenador' || userRole === 'scout interne' || userRole === 'scout externe' || userRole === 'scout interne cantera' || userRole === 'scout externe cantera' ? (
                    <Typography
                      sx={{
                        fontSize: '0.95rem',
                        color: 'text.primary',
                        fontWeight: 400
                      }}
                    >
                      {player.agencia}
                    </Typography>
                  ) : (
                    <a
                      href={`/agencia?agency=${encodeURIComponent(player.agencia)}`}
                      style={{
                        textDecoration: 'none',
                        color: '#1976d2',
                        fontSize: '0.95rem',
                        fontWeight: 400
                      }}
                    >
                      {player.agencia}
                    </a>
                  )}
                </Box>
              </Grid>
            )}
          {!!player.rangoPrecios && (
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em'
                  }}
                >
                  {t('createPlayer.fields.salaryRange')}:
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.95rem',
                    color: 'text.primary',
                    fontWeight: 400
                  }}
                >
                  {player.rangoPrecios}
                </Typography>
              </Box>
            </Grid>
          )}
          {!!player.contrato && (
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em'
                  }}
                >
                  {t('createPlayer.fields.contract')}:
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.95rem',
                    color: 'text.primary',
                    fontWeight: 400
                  }}
                >
                  {t(`createPlayer.contractDurations.${player.contrato}`, player.contrato)}
                </Typography>
              </Box>
            </Grid>
          )}
          {!!player.buyout && (
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em'
                  }}
                >
                  {t('createPlayer.fields.buyout')}:
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.95rem',
                    color: 'text.primary',
                    fontWeight: 400
                  }}
                >
                  {player.buyout}
                </Typography>
              </Box>
            </Grid>
            )}
          </Grid>
        </Box>
      )}
    </Paper>
  );
}

export default InformacionContractual;
